import moment from 'moment'
import React from 'react'

import { IEnrollmentApplication } from 'main/gql/enrollmentApplication'
import { IHydratedLead } from 'main/gql/lead'
import { applicationPath } from 'main/routes'
import { palette } from 'main/ui'
import { DATE_HUMAN_FORMAT, formatDateHuman } from 'main/util/date'
import { dateTimeIsUpcoming } from 'main/util/dateTime'
import {
  filterActiveEnrollments,
  findLatestEnrollmentApplication,
  findLatestTour,
  findLatestTourRequest,
  findLatestUnenrolledEnrollment,
  findPendingEnrollment,
} from './lead'

import { ActionLink, ActionNativeLink, ActionText } from './ActionLinks.styles'

interface IProps {
  lead: IHydratedLead
  mapView?: boolean
  onRequestOpenScheduleTourModal: () => void
  onRequestOpenCancelTourRequestModal: () => void
}

export default function ActionLinks(props: IProps) {
  const { lead, mapView, onRequestOpenScheduleTourModal, onRequestOpenCancelTourRequestModal } =
    props
  const { enrollments, enrollmentApplications, tours, tourRequests } = lead

  const pendingEnrollment = findPendingEnrollment(enrollments)
  if (pendingEnrollment && pendingEnrollment.offerCode) {
    return <PendingEnrollment mapView={mapView} enrollmentOfferCode={pendingEnrollment.offerCode} />
  }

  const activeEnrollments = filterActiveEnrollments(enrollments)
  if (activeEnrollments.length) {
    return <Enrolled mapView={mapView} lead={lead} />
  }

  const latestUnenrolledEnrollment = findLatestUnenrolledEnrollment(enrollments)
  if (latestUnenrolledEnrollment && latestUnenrolledEnrollment.endsOn) {
    return <Unenrolled mapView={mapView} enrollmentEndsOn={latestUnenrolledEnrollment.endsOn} />
  }

  const latestEnrollmentApplication = findLatestEnrollmentApplication(enrollmentApplications)
  if (latestEnrollmentApplication) {
    return (
      <PendingEnrollmentApplication
        mapView={mapView}
        enrollmentApplication={latestEnrollmentApplication}
      />
    )
  }

  const latestTour = findLatestTour(tours)
  if (latestTour) {
    if (dateTimeIsUpcoming(latestTour.beginsAt)) {
      return (
        <UpcomingTour
          mapView={mapView}
          onRequestOpenScheduleTourModal={onRequestOpenScheduleTourModal}
        />
      )
    } else {
      return (
        <PreviousTour
          mapView={mapView}
          lead={lead}
          onRequestOpenScheduleTourModal={onRequestOpenScheduleTourModal}
        />
      )
    }
  }

  const latestTourRequest = findLatestTourRequest(tourRequests)
  if (latestTourRequest) {
    return (
      <CancelTourRequest
        mapView={mapView}
        onRequestOpenCancelTourRequestModal={onRequestOpenCancelTourRequestModal}
      />
    )
  }

  return (
    <Default mapView={mapView} onRequestOpenScheduleTourModal={onRequestOpenScheduleTourModal} />
  )
}

function Enrolled(props: { lead: IHydratedLead; mapView?: boolean }) {
  const { lead, mapView } = props
  const user = window.lego.user

  return (
    <div>
      <ActionNativeLink
        mapView={mapView}
        href={'/handbooks/' + lead.school.slug}
        css={{ marginRight: '3px' }}
      >
        Parent Handbook
      </ActionNativeLink>
      ·
      <ActionNativeLink
        mapView={mapView}
        href={`/me/${user.id}?schoolId=${lead.schoolId}`}
        css={{ marginLeft: '3px' }}
      >
        Enrollment Info
      </ActionNativeLink>
    </div>
  )
}

function Unenrolled(props: { enrollmentEndsOn: string; mapView?: boolean }) {
  const { enrollmentEndsOn, mapView } = props

  return (
    <ActionText mapView={mapView} css={{ color: palette.fern2 }}>
      Unenrolled {formatDateHuman(enrollmentEndsOn)}
    </ActionText>
  )
}

function PendingEnrollment(props: { enrollmentOfferCode: string; mapView?: boolean }) {
  const { enrollmentOfferCode, mapView } = props

  return (
    <ActionNativeLink mapView={mapView} href={`/offer/${enrollmentOfferCode}`}>
      View enrollment invitation
    </ActionNativeLink>
  )
}

function PendingEnrollmentApplication(props: {
  enrollmentApplication: IEnrollmentApplication
  mapView?: boolean
}) {
  const { enrollmentApplication, mapView } = props

  return (
    <ActionText mapView={mapView} css={{ color: palette.fern2 }}>
      Application submitted {moment(enrollmentApplication.insertedAt).format(DATE_HUMAN_FORMAT)}
    </ActionText>
  )
}

function UpcomingTour(props: { onRequestOpenScheduleTourModal: () => void; mapView?: boolean }) {
  const { onRequestOpenScheduleTourModal, mapView } = props

  return (
    <ActionNativeLink mapView={mapView} onClick={() => onRequestOpenScheduleTourModal()}>
      Cancel or reschedule
    </ActionNativeLink>
  )
}

function PreviousTour(props: {
  lead: IHydratedLead
  onRequestOpenScheduleTourModal: () => void
  mapView?: boolean
}) {
  const { lead, mapView, onRequestOpenScheduleTourModal } = props

  return (
    <div>
      <ActionNativeLink
        mapView={mapView}
        onClick={() => onRequestOpenScheduleTourModal()}
        css={{ marginRight: '3px' }}
      >
        Tour Again
      </ActionNativeLink>
      ·
      <ActionLink mapView={mapView} to={applicationPath(lead.schoolId)} css={{ marginLeft: '3px' }}>
        Apply
      </ActionLink>
    </div>
  )
}

function CancelTourRequest(props: {
  onRequestOpenCancelTourRequestModal: () => void
  mapView?: boolean
}) {
  const { mapView, onRequestOpenCancelTourRequestModal } = props

  return (
    <ActionNativeLink mapView={mapView} onClick={() => onRequestOpenCancelTourRequestModal()}>
      Cancel Tour Request
    </ActionNativeLink>
  )
}

function Default(props: { onRequestOpenScheduleTourModal: () => void; mapView?: boolean }) {
  const { mapView, onRequestOpenScheduleTourModal } = props

  return (
    <ActionNativeLink mapView={mapView} onClick={() => onRequestOpenScheduleTourModal()}>
      Schedule Tour
    </ActionNativeLink>
  )
}
